import { helpWhy } from '../../help/why';
import React, { useState } from 'react';
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, TextField,
} from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { CommercialPartyService } from '../../services/commercialParty.service';
import {
    COMMERCIAL_PARTY_TYPE_LABELS, COMMERCIAL_PARTY_TYPES, CommercialPartyType, ICommercialParty,
    ICreateCommercialPartyRequest,
} from '../../interfaces/commercialParty.interface';
import { humanizeEnum } from '../../utils/format';

/** Fix round 1 (concern 1): falls back to the raw enum value for any type without a friendlier
 * override in `COMMERCIAL_PARTY_TYPE_LABELS`. */
const partyTypeLabel = (type: CommercialPartyType): string => COMMERCIAL_PARTY_TYPE_LABELS[type] ?? humanizeEnum(type);

interface Props {
    institutionId: string;
    target: ICommercialParty | null;
    onClose: () => void;
    onSaved: () => void;
}

interface FormState {
    partyType: CommercialPartyType;
    code: string;
    name: string;
    contactPerson: string;
    email: string;
    phone: string;
    address: string;
    countryCode: string;
    swiftCode: string;
    branch: string;
    licenseNo: string;
    remarks: string;
}

const EMPTY: FormState = {
    partyType: 'BANK', code: '', name: '', contactPerson: '', email: '', phone: '', address: '',
    countryCode: '', swiftCode: '', branch: '', licenseNo: '', remarks: '',
};

/** Create/edit a commercial party (Task 4a, spec §2, 7.4). Bank-only fields (swift/branch) are
 * shown only when `partyType === 'BANK'`, matching `CommercialPartyDTO`'s field set exactly (R-15)
 * -- mirrors `BondLicenseDialog`'s dialog shape. */
const CommercialPartyFormDialog: React.FC<Props> = ({ institutionId, target, onClose, onSaved }) => {
    const toast = useToast();
    const isEdit = !!target;
    const [form, setForm] = useState<FormState>(
        target
            ? {
                partyType: target.partyType, code: target.code, name: target.name,
                contactPerson: target.contactPerson ?? '', email: target.email ?? '',
                phone: target.phone ?? '', address: target.address ?? '',
                countryCode: target.countryCode ?? '', swiftCode: target.swiftCode ?? '',
                branch: target.branch ?? '', licenseNo: target.licenseNo ?? '',
                remarks: target.remarks ?? '',
            }
            : { ...EMPTY }
    );
    const [saving, setSaving] = useState(false);

    const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setForm(f => ({ ...f, [k]: e.target.value }));

    const isBank = form.partyType === 'BANK';
    const valid = form.code.trim().length > 0 && form.code.length <= 32 && form.name.trim().length > 0 && form.name.length <= 160;

    const saveWhy = saving ? null
        : form.code.trim().length === 0 ? 'Enter the party code.'
        : form.code.length > 32 ? 'The code can be 32 characters at most.'
        : form.name.trim().length === 0 ? 'Enter the party name.'
        : form.name.length > 160 ? 'The name can be 160 characters at most.'
        : null;

    const handleSave = async () => {
        const payload: ICreateCommercialPartyRequest = {
            partyType: form.partyType,
            code: form.code.trim(),
            name: form.name.trim(),
            contactPerson: form.contactPerson.trim() || undefined,
            email: form.email.trim() || undefined,
            phone: form.phone.trim() || undefined,
            address: form.address.trim() || undefined,
            countryCode: form.countryCode.trim() || undefined,
            swiftCode: isBank ? (form.swiftCode.trim() || undefined) : undefined,
            branch: isBank ? (form.branch.trim() || undefined) : undefined,
            licenseNo: form.licenseNo.trim() || undefined,
            remarks: form.remarks.trim() || undefined,
        };
        try {
            setSaving(true);
            if (isEdit && target) {
                await CommercialPartyService.update(institutionId, target.id, payload);
                toast.showSuccess('Commercial party updated');
            } else {
                await CommercialPartyService.create(institutionId, payload);
                toast.showSuccess('Commercial party created');
            }
            onSaved();
        } catch (e) {
            toast.showApiError(e, 'Failed to save commercial party');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'commercial.partyForm.page' } as any}>
            <DialogTitle>{isEdit ? 'Edit Commercial Party' : 'New Commercial Party'}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ pt: 1 }} data-help-section="commercial.partyForm.section.details">
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            data-help="commercial.partyForm.field.type" select label="Type" fullWidth required value={form.partyType}
                            onChange={set('partyType')}
                        >
                            {COMMERCIAL_PARTY_TYPES.map(t => <MenuItem key={t} value={t}>{partyTypeLabel(t)}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField data-help="commercial.partyForm.field.code" label="Code" fullWidth required value={form.code} onChange={set('code')} />
                    </Grid>
                    <Grid size={12}>
                        <TextField data-help="commercial.partyForm.field.name" label="Name" fullWidth required value={form.name} onChange={set('name')} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField data-help="commercial.partyForm.field.contactPerson" label="Contact Person" fullWidth value={form.contactPerson} onChange={set('contactPerson')} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField data-help="commercial.partyForm.field.email" label="Email" fullWidth value={form.email} onChange={set('email')} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField data-help="commercial.partyForm.field.phone" label="Phone" fullWidth value={form.phone} onChange={set('phone')} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField data-help="commercial.partyForm.field.countryCode" label="Country Code" fullWidth inputProps={{ maxLength: 2 }} value={form.countryCode} onChange={set('countryCode')} />
                    </Grid>
                    <Grid size={12}>
                        <TextField data-help="commercial.partyForm.field.address" label="Address" fullWidth multiline minRows={2} value={form.address} onChange={set('address')} />
                    </Grid>
                    {isBank && (
                        <>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField data-help="commercial.partyForm.field.swiftCode" label="SWIFT Code" fullWidth value={form.swiftCode} onChange={set('swiftCode')} />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField data-help="commercial.partyForm.field.branch" label="Branch" fullWidth value={form.branch} onChange={set('branch')} />
                            </Grid>
                        </>
                    )}
                    <Grid size={12}>
                        <TextField data-help="commercial.partyForm.field.licenseNo" label="License No" fullWidth value={form.licenseNo} onChange={set('licenseNo')} />
                    </Grid>
                    <Grid size={12}>
                        <TextField data-help="commercial.partyForm.field.remarks" label="Remarks" fullWidth multiline minRows={2} value={form.remarks} onChange={set('remarks')} />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button data-help="commercial.partyForm.cancel" onClick={onClose}>Cancel</Button>
                <Button data-help="commercial.partyForm.save" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} variant="contained" disabled={saving || !valid} onClick={handleSave}>Save</Button>
            </DialogActions>
        </Dialog>
    );
};

export default CommercialPartyFormDialog;
